import { FC, useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { apiLogoutUser, apiGetMyUnreadNotifications } from '../ui_components/ApiList';
import { useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import MenuUserDesktop from './MenuUserDesktop';
import { useLocation, useNavigate } from 'react-router-dom';
import colors from '../../styles/colors';
import type { User } from '../../types';

const Container = styled.div`
  & .section_name {
    margin-right: 10px !important;
  }

  & .font-order-type {
    color: white;
    font-size: 25px;
  }

  & .box_menu_icon {
    position: relative;
  }

  & .menu-icon:hover {
    cursor: pointer;
    color: gold;
  }
`;

const UnreadBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  color: white;
  background-color: ${colors.bgColorRed};
  border-radius: 50%;
  border: 1px solid white;
`;

const BoxIconDesktop = styled.div``;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  color: white;
  font-size: 18px;
`;

interface InfoUserLoggedHeaderProps {
  user: User;
}

const POLL_UNREAD_INTERVAL_MS = 30000;

const InfoUserLoggedHeader: FC<InfoUserLoggedHeaderProps> = ({ user }) => {
  const [visibilityMenuDesktop, setVisibilityMenuDesktop] = useState<boolean>(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const prevLocationRef = useRef<string>(location.pathname);

  const dispatch = useDispatch();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiGetMyUnreadNotifications();
      setUnreadCount(res.data?.length ?? 0);
    } catch {
      // noop — mantieni il conteggio precedente in caso di errore
    }
  }, []);

  const hasFetchedUnread = useRef(false);
  useEffect(() => {
    if (!hasFetchedUnread.current) {
      hasFetchedUnread.current = true;
      fetchUnreadCount();
    }
    const interval = setInterval(fetchUnreadCount, POLL_UNREAD_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleEnterMenu = (): void => {
    setVisibilityMenuDesktop(true);
  };

  const handleLeaveMenu = (): void => {
    setVisibilityMenuDesktop(false);
  };

  const handleLogOutClick = async (): Promise<void> => {
    try {
      await apiLogoutUser();
      dispatch(logout());

      // Se l'utente è in una pagina admin, reindirizza alla homepage
      if (location.pathname.startsWith('/admin')) {
        navigate('/');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavigationStart = (): void => {
    setIsNavigating(true);
  };

  // Reset loading quando la location cambia (navigazione completata)
  useEffect(() => {
    if (prevLocationRef.current !== location.pathname) {
      prevLocationRef.current = location.pathname;
      setIsNavigating(false);
    }
  }, [location.pathname]);

  return (
    <>
      {isNavigating && (
        <LoadingOverlay>
          <div>Caricamento...</div>
        </LoadingOverlay>
      )}
      <Container className="InfoUserLoggedSection d-flex align-items-center">
        <p className="section_name">{user.username}</p>

        {/* Desktop */}
        <BoxIconDesktop className="box_menu_icon d-none d-md-inline-block">
          <FontAwesomeIcon
            icon={faBars}
            className="font-order-type menu-icon"
            onMouseEnter={() => handleEnterMenu()}
          />
          {unreadCount > 0 && (
            <UnreadBadge title={`${unreadCount} notifiche non lette`}>{unreadCount}</UnreadBadge>
          )}
          {visibilityMenuDesktop && (
            <MenuUserDesktop
              user={user}
              handleLeaveMenu={handleLeaveMenu}
              handleLogOutClick={handleLogOutClick}
              onNavigationStart={handleNavigationStart}
              unreadNotificationsCount={unreadCount}
              onNotificationsRead={fetchUnreadCount}
            />
          )}
        </BoxIconDesktop>
      </Container>
    </>
  );
};

export default InfoUserLoggedHeader;
